import * as React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Collapse from '@mui/material/Collapse';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import { versoes } from '../data/changelog';

// Ícones
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Logout from '@mui/icons-material/Logout';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { RELATORIOS } from '../config/relatorios';

// Serviços
import { removeSessionCookie, getSessionCookie, isPsicologo, getIdUsuario } from '../services/auth';
import { tituloDaRota, trilhaDaRota } from '../services/navegacao';
import { validarSessao, logoutUsuario } from '../services/api';
import useImagemBlobUrl from '../hooks/useImagemBlobUrl';

const decodeToken = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(base64Url.length / 4) * 4, '=');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Erro ao decodificar token:', error);
        return null;
    }
};

// 224px: a sidebar perdia ~16px so em folga horizontal dos itens. Abaixo de
// 210 os rotulos de dois termos ("Status do Sistema") comecam a quebrar.
const drawerWidth = 224;

function MainLayout(props) {
    const { window: windowProp } = props;
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [modoFoco, setModoFoco] = React.useState(false);
    const [novidadesNaoVistas, setNovidadesNaoVistas] = React.useState(false);

    React.useEffect(() => {
        const ultimaVista = localStorage.getItem('sigapsi:ultimaVersaoVista');
        const versaoAtual = versoes[0]?.versao;
        setNovidadesNaoVistas(versaoAtual && ultimaVista !== versaoAtual);
        const onSeen = () => setNovidadesNaoVistas(false);
        window.addEventListener('sigapsi:novidadesVistas', onSeen);
        return () => window.removeEventListener('sigapsi:novidadesVistas', onSeen);
    }, []);

    React.useEffect(() => {
        const onFoco = (e) => setModoFoco(!!e.detail);
        window.addEventListener('sigapsi:modoFoco', onFoco);
        return () => window.removeEventListener('sigapsi:modoFoco', onFoco);
    }, []);

    const openUserMenu = Boolean(anchorEl);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();

    // Inicializa o dropdown aberto se a rota atual pertencer a ele
    const [openConfig, setOpenConfig] = React.useState(
        ['/parametros', '/usuarios', '/status-sistema'].some(path => location.pathname.startsWith(path))
    );
    const [openAssinaturas, setOpenAssinaturas] = React.useState(
        ['/assinaturas-paciente', '/planos-atendimento', '/periodicidades'].some(path => location.pathname.startsWith(path))
    );
    const [openConvenios, setOpenConvenios] = React.useState(
        ['/convenios', '/guias', '/lotes-faturamento'].some(path => location.pathname.startsWith(path))
    );
    const [openFinanceiro, setOpenFinanceiro] = React.useState(
        ['/contas-pagar', '/contas-receber', '/conta-bancaria', '/plano-pagamento', '/financeiro/historico'].some(path => location.pathname.startsWith(path))
    );
    const [openDocumentos, setOpenDocumentos] = React.useState(
        ['/documentos/gerar', '/documentos/historico', '/documentos/modelos'].some(path => location.pathname.startsWith(path))
    );
    const [openAgendamentos, setOpenAgendamentos] = React.useState(
        ['/agendamentos/calendario', '/agendamentos/disponibilidade', '/agendamentos/fila-espera'].some(path => location.pathname.startsWith(path))
    );
    const [openClinico, setOpenClinico] = React.useState(
        ['/prontuarios', '/anamnese', '/psicologo'].some(path => location.pathname.startsWith(path))
    );

    const buildUserData = (decoded, fallbackName, fallbackUsername) => {
        const name = (decoded?.nome_completo || fallbackName) || 'Usuário';
        const username = (decoded?.sub || fallbackUsername) || '';
        const fotoFromToken = decoded?.foto_url || null;
        const fotoFromStorage = localStorage.getItem('user_foto_url');
        return {
            name,
            username,
            fotoUrl: fotoFromStorage || fotoFromToken,
            initial: name.charAt(0).toUpperCase(),
        };
    };

    const [userData, setUserData] = React.useState(() => {
        const token = getSessionCookie();
        const storedName = localStorage.getItem('user_name');
        const storedUsername = localStorage.getItem('user_login');
        if (token) {
            const decoded = decodeToken(token);
            if (decoded) return buildUserData(decoded, storedName, storedUsername);
        }
        if (storedName) return buildUserData(null, storedName, storedUsername);
        return { name: 'Psicólogo', username: '', fotoUrl: localStorage.getItem('user_foto_url'), initial: 'P' };
    });

    React.useEffect(() => {
        const verificarAutenticidade = async () => {
            const token = getSessionCookie();
            if (token) {
                try {
                    const sessionData = await validarSessao();
                    const decoded = decodeToken(token);
                    if (decoded) {
                        const fotoUrl = sessionData?.fotoUrl || null;
                        setUserData(prev => ({
                            ...buildUserData(decoded, prev.name, prev.username),
                            ...(fotoUrl ? { fotoUrl } : {})
                        }));
                    }
                } catch (error) {
                    console.warn("Sessão inválida ou substituída em outro dispositivo.");
                    removeSessionCookie();
                    navigate('/login');
                }
            } else {
                navigate('/login');
            }
        };
        verificarAutenticidade();
    }, [navigate]);

    React.useEffect(() => {
        const onPhotoUpdated = () => {
            const novaUrl = localStorage.getItem('user_foto_url');
            setUserData(prev => ({ ...prev, fotoUrl: novaUrl }));
        };
        window.addEventListener('profilePhotoUpdated', onPhotoUpdated);
        return () => window.removeEventListener('profilePhotoUpdated', onPhotoUpdated);
    }, []);

    /**
     * Foto do usuário. `userData.fotoUrl` guarda o objectName do MinIO
     * (`principal/uuid_foto.jpg`), não um endereço — usá-lo direto no `src`
     * pediria esse caminho ao servidor do front.
     */
    const idUsuario = getIdUsuario();
    const fotoBlobUrl = useImagemBlobUrl(
        idUsuario ? `/usuarios/${idUsuario}/foto` : null,
        userData.fotoUrl,
    );

    const handleUserClick = (event) => setAnchorEl(event.currentTarget);
    const handleUserClose = () => setAnchorEl(null);

    const handleLogout = async () => {
        try {
            await logoutUsuario();
        } catch (error) {
            console.error("Erro ao invalidar sessão no servidor", error);
        } finally {
            localStorage.removeItem('user_foto_url');
            localStorage.clear();
            removeSessionCookie();
            navigate('/login');
        }
    };

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
    const handleConfigClick = () => setOpenConfig(!openConfig);
    const handleAssinaturasClick = () => setOpenAssinaturas(!openAssinaturas);
    const handleConveniosClick = () => setOpenConvenios(!openConvenios);
    const handleFinanceiroClick = () => setOpenFinanceiro(!openFinanceiro);
    const handleDocumentosClick = () => setOpenDocumentos(!openDocumentos);
    const handleAgendamentosClick = () => setOpenAgendamentos(!openAgendamentos);
    const handleClinicoClick = () => setOpenClinico(!openClinico);

    /**
     * Menu principal enxuto (achado L1): Hoje, Pacientes e Atendimentos no primeiro
     * nivel; o resto agrupado. Antes o primeiro nivel listava seis assuntos lado a
     * lado (pacientes, prontuarios, anamnese, sessoes, psicologo) sem
     * hierarquia. Prontuario e anamnese seguem no grupo "Clinico" e tambem
     * aparecem, em contexto, na ficha do paciente.
     */
    const mainMenuItems = [
        { text: 'Hoje', icon: <DashboardOutlinedIcon />, path: '/dashboard' },
        { text: 'Pacientes', icon: <GroupOutlinedIcon />, path: '/pacientes' },
        { text: 'Atendimentos', icon: <EventAvailableIcon />, path: '/sessoes' },
    ];

    const clinicoMenuItems = [
        { text: 'Prontuários', icon: <FolderSharedIcon />, path: '/prontuarios' },
        { text: 'Anamnese', icon: <ContentPasteIcon />, path: '/anamnese' },
        ...(isPsicologo() ? [{ text: 'Psicólogo', icon: <PsychologyIcon />, path: '/psicologo' }] : []),
    ];

    // Gerado da mesma lista do hub e das rotas (config/relatorios.js): relatório
    // novo aparece no menu sem edição aqui. Ícone sem mapa cai no genérico.
    const ICONES_RELATORIO = {
        psicologos: <PsychologyIcon />,
        pacientes: <GroupOutlinedIcon />,
        'contas-receber': <AttachMoneyIcon />,
        'sessoes-periodo': <EventAvailableIcon />,
        'sessoes-paciente': <HistoryIcon />,
        'agenda-ocupacao': <CalendarMonthIcon />,
        'fila-espera': <FormatListBulletedIcon />,
        'faltas-cancelamentos': <EventAvailableIcon />,
        'contas-pagar': <AttachMoneyIcon />,
        'pagamentos-periodo': <AttachMoneyIcon />,
    };

    /**
     * Não é mais menu: o drawer tem um único item "Relatórios", que abre o
     * catálogo. Quarenta itens numa gaveta lateral custavam mais rolagem do
     * que a busca do catálogo resolve, e repetiam a lista que a própria página
     * mostra. Serve ainda para o título da página em /relatorios/<chave>.
     */
    const relatoriosMenuItems = [
        { text: 'Relatórios', icon: <AssessmentOutlinedIcon />, path: '/relatorios', exact: true },
        { text: 'Gráficos de atendimento', icon: <AssessmentOutlinedIcon />, path: '/relatorios/graficos' },
        ...RELATORIOS.map(r => ({
            text: r.titulo,
            icon: ICONES_RELATORIO[r.key] ?? <AssessmentOutlinedIcon />,
            path: `/relatorios/${r.key}`,
        })),
    ];

    const configMenuItems = [
        { text: 'Parâmetros', icon: <SettingsOutlinedIcon />, path: '/parametros' },
        { text: 'Usuários do Sistema', icon: <PersonOutlinedIcon />, path: '/usuarios' },
        { text: 'Status do Sistema', icon: <HealthAndSafetyIcon />, path: '/status-sistema' },
        { text: 'Backup', icon: <CloudDownloadOutlinedIcon />, path: '/backup' },
    ];

    const assinaturasMenuItems = [
        { text: 'Pacientes', icon: <DescriptionIcon />, path: '/assinaturas-paciente' },
        { text: 'Histórico de Renovações', icon: <EventAvailableIcon />, path: '/assinaturas-paciente/renovacoes' },
        { text: 'Planos de Atendimento', icon: <DescriptionIcon />, path: '/planos-atendimento' },
        { text: 'Periodicidades', icon: <EventAvailableIcon />, path: '/periodicidades' },
    ];

    const documentosMenuItems = [
        { text: 'Gerar Documento', icon: <AssignmentIcon />, path: '/documentos/gerar' },
        { text: 'Histórico', icon: <HistoryIcon />, path: '/documentos/historico' },
        { text: 'Modelos e Templates', icon: <AutoFixHighIcon />, path: '/documentos/modelos' },
    ];

    const agendamentosMenuItems = [
        { text: 'Calendário', icon: <CalendarMonthIcon />, path: '/agendamentos/calendario' },
        { text: 'Disponibilidade', icon: <ScheduleIcon />, path: '/agendamentos/disponibilidade' },
        { text: 'Fila de Espera', icon: <FormatListBulletedIcon />, path: '/agendamentos/fila-espera' },
    ];

    const conveniosMenuItems = [
        { text: 'Convênios', icon: <LocalHospitalIcon />, path: '/convenios' },
        { text: 'Guias', icon: <DescriptionIcon />, path: '/guias' },
        { text: 'Lotes de Faturamento', icon: <InventoryIcon />, path: '/lotes-faturamento' },
    ];

    const financeiroMenuItems = [
        { text: 'Contas a Pagar', path: '/contas-pagar' },
        { text: 'Contas a Receber', path: '/contas-receber' },
        { text: 'Conta Bancária', path: '/conta-bancaria' },
        { text: 'Plano de Pagamento', path: '/plano-pagamento' },
        { text: 'Histórico', icon: <HistoryIcon />, path: '/financeiro/historico' },
    ];

    /**
     * Titulo da pagina. Antes so reconhecia caminhos exatos presentes nos menus, e
     * qualquer rota com ID (/pacientes/12, /sessoes/5/evolucao) exibia "SigaPsi".
     * Agora vem do mapa de rotas, que tambem monta a trilha contextual.
     */
    const getPageTitle = () => {
        const item = mainMenuItems.find(i => i.path === location.pathname)
            || clinicoMenuItems.find(i => i.path === location.pathname)
            || configMenuItems.find(i => i.path === location.pathname)
            || assinaturasMenuItems.find(i => i.path === location.pathname)
            || conveniosMenuItems.find(i => i.path === location.pathname)
            || financeiroMenuItems.find(i => i.path === location.pathname)
            || documentosMenuItems.find(i => i.path === location.pathname)
            || agendamentosMenuItems.find(i => i.path === location.pathname)
            || relatoriosMenuItems.find(i => i.path === location.pathname);
        if (item) return item.text;
        return tituloDaRota(location.pathname);
    };

    // Trilha: Pacientes > Maria > Evolucao, com os niveis intermediarios clicaveis.
    const trilha = trilhaDaRota(location.pathname);

    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Logo */}
            <Box sx={{ height: 64, display: 'flex', alignItems: 'center', px: 2, gap: 1.25 }}>
                <PsychologyIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.contrastText', fontSize: '1.1rem' }}>
                    SigaPsi
                </Typography>
            </Box>

            {/* Lista Menu */}
            <List
                sx={{
                    px: 1,
                    py: 0.5,
                    flexGrow: 1,
                    '& .MuiListItemButton-root': { px: 1.25, py: 0.5 },
                    '& .MuiListItemIcon-root': { justifyContent: 'center' },
                    '& .MuiListItemIcon-root .MuiSvgIcon-root': { fontSize: 20 },
                    '& .MuiCollapse-root .MuiListItemIcon-root .MuiSvgIcon-root': { fontSize: 18 },
                }}
            >
                {mainMenuItems.map((item) => {
                    const isActive = location.pathname.startsWith(item.path);
                    return (
                        <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.375 }}>
                            <ListItemButton
                                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                                sx={{
                                    borderRadius: '8px',
                                    minHeight: 42,
                                    color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.7)',
                                    bgcolor: isActive ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                                    boxShadow: isActive ? 'inset 3px 0 0 0 #1ABB9C' : 'none',
                                    '&:hover': {
                                        bgcolor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                                        color: 'primary.main'
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ color: 'inherit', minWidth: '34px' }}>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isActive ? 600 : 400 }} />
                            </ListItemButton>
                        </ListItem>
                    );
                })}

                {/* Menu Agendamentos (Dropdown) */}
                {/* Menu Clinico (Dropdown) — prontuario e anamnese saem do
                    primeiro nivel sem perder acesso. */}
                <ListItem disablePadding sx={{ display: 'block', mb: 0.375, mt: 0.75 }}>
                    <ListItemButton
                        onClick={handleClinicoClick}
                        aria-expanded={openClinico}
                        sx={{
                            borderRadius: '8px',
                            minHeight: 42,
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                        }}
                    >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: '34px' }}>
                            <FolderSharedIcon />
                        </ListItemIcon>
                        <ListItemText primary="Clínico" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 400 }} />
                        {openClinico ? <ExpandLess sx={{ fontSize: '1.2rem' }} /> : <ExpandMore sx={{ fontSize: '1.2rem' }} />}
                    </ListItemButton>

                    <Collapse in={openClinico} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {clinicoMenuItems.map((item) => {
                                const isActive = location.pathname.startsWith(item.path);
                                return (
                                    <ListItemButton
                                        key={item.text}
                                        onClick={() => { navigate(item.path); setMobileOpen(false); }}
                                        sx={{
                                            borderRadius: '8px',
                                            minHeight: 36,
                                            pl: 3.5,
                                            mb: 0.375,
                                            color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.7)',
                                            bgcolor: isActive ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                                            '&:hover': { bgcolor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: 'inherit', minWidth: '30px' }}>
                                            {React.cloneElement(item.icon, { fontSize: 'small', sx: { fontSize: 18 } })}
                                        </ListItemIcon>
                                        <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActive ? 600 : 400 }} />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Collapse>
                </ListItem>

                <ListItem disablePadding sx={{ display: 'block', mb: 0.375, mt: 0.75 }}>
                    <ListItemButton
                        onClick={handleAgendamentosClick}
                        sx={{
                            borderRadius: '8px',
                            minHeight: 42,
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                        }}
                    >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: '34px' }}>
                            <CalendarMonthIcon />
                        </ListItemIcon>
                        <ListItemText primary="Agenda" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 400 }} />
                        {openAgendamentos ? <ExpandLess sx={{ fontSize: '1.2rem' }} /> : <ExpandMore sx={{ fontSize: '1.2rem' }} />}
                    </ListItemButton>

                    <Collapse in={openAgendamentos} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {agendamentosMenuItems.map((item) => {
                                const isActive = location.pathname.startsWith(item.path);
                                return (
                                    <ListItemButton
                                        key={item.text}
                                        onClick={() => { navigate(item.path); setMobileOpen(false); }}
                                        sx={{
                                            borderRadius: '8px',
                                            minHeight: 36,
                                            pl: 3.5,
                                            mb: 0.375,
                                            color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.7)',
                                            bgcolor: isActive ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                                            '&:hover': { bgcolor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: 'inherit', minWidth: '30px' }}>
                                            {React.cloneElement(item.icon, { fontSize: 'small', sx: { fontSize: 18 } })}
                                        </ListItemIcon>
                                        <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActive ? 600 : 400 }} />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Collapse>
                </ListItem>

                {/* Menu Assinaturas (Dropdown) */}
                <ListItem disablePadding sx={{ display: 'block', mb: 0.375, mt: 0.75 }}>
                    <ListItemButton
                        onClick={handleAssinaturasClick}
                        sx={{
                            borderRadius: '8px',
                            minHeight: 42,
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                        }}
                    >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: '34px' }}>
                            <DescriptionIcon />
                        </ListItemIcon>
                        <ListItemText primary="Assinaturas" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 400 }} />
                        {openAssinaturas ? <ExpandLess sx={{ fontSize: '1.2rem' }} /> : <ExpandMore sx={{ fontSize: '1.2rem' }} />}
                    </ListItemButton>

                    <Collapse in={openAssinaturas} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {assinaturasMenuItems.map((item) => {
                                const isActive = location.pathname.startsWith(item.path);
                                return (
                                    <ListItemButton
                                        key={item.text}
                                        onClick={() => { navigate(item.path); setMobileOpen(false); }}
                                        sx={{
                                            borderRadius: '8px',
                                            minHeight: 36,
                                            pl: 3.5,
                                            mb: 0.375,
                                            color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.7)',
                                            bgcolor: isActive ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                                            '&:hover': { bgcolor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: 'inherit', minWidth: '30px' }}>
                                            {React.cloneElement(item.icon, { fontSize: 'small', sx: { fontSize: 18 } })}
                                        </ListItemIcon>
                                        <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActive ? 600 : 400 }} />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Collapse>
                </ListItem>

                {/* Menu Documentos (Dropdown) */}
                <ListItem disablePadding sx={{ display: 'block', mb: 0.375, mt: 0.75 }}>
                    <ListItemButton
                        onClick={handleDocumentosClick}
                        sx={{
                            borderRadius: '8px', minHeight: 42,
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                        }}
                    >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: '34px' }}>
                            <AssignmentIcon /> {/* <-- Ícone alterado aqui! */}
                        </ListItemIcon>
                        <ListItemText primary="Documentos" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 400 }} />
                        {openDocumentos ? <ExpandLess sx={{ fontSize: '1.2rem' }} /> : <ExpandMore sx={{ fontSize: '1.2rem' }} />}
                    </ListItemButton>

                    <Collapse in={openDocumentos} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {documentosMenuItems.map((item) => {
                                const isActive = location.pathname.startsWith(item.path);
                                return (
                                    <ListItemButton
                                        key={item.text}
                                        onClick={() => { navigate(item.path); setMobileOpen(false); }}
                                        sx={{
                                            borderRadius: '8px', minHeight: 36, pl: 3.5, mb: 0.5,
                                            color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.7)',
                                            bgcolor: isActive ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                                            '&:hover': { bgcolor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: 'inherit', minWidth: '30px' }}>
                                            {/* O ícone de cada sub-menu é desenhado aqui */}
                                            {item.icon ? React.cloneElement(item.icon, { fontSize: 'small', sx: { fontSize: 18 } }) : null}
                                        </ListItemIcon>
                                        <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActive ? 600 : 400 }} />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Collapse>
                </ListItem>
                {/* Menu Convenios (Dropdown) — cadastro, guias e lotes de faturamento. */}
                <ListItem disablePadding sx={{ display: 'block', mb: 0.375, mt: 0.75 }}>
                    <ListItemButton
                        onClick={handleConveniosClick}
                        sx={{
                            borderRadius: '8px',
                            minHeight: 42,
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                        }}
                    >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: '34px' }}>
                            <LocalHospitalIcon />
                        </ListItemIcon>
                        <ListItemText primary="Convênios" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 400 }} />
                        {openConvenios ? <ExpandLess sx={{ fontSize: '1.2rem' }} /> : <ExpandMore sx={{ fontSize: '1.2rem' }} />}
                    </ListItemButton>

                    <Collapse in={openConvenios} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {conveniosMenuItems.map((item) => {
                                const isActive = location.pathname.startsWith(item.path);
                                return (
                                    <ListItemButton
                                        key={item.text}
                                        onClick={() => { navigate(item.path); setMobileOpen(false); }}
                                        sx={{
                                            borderRadius: '8px',
                                            minHeight: 36,
                                            pl: 3.5,
                                            mb: 0.375,
                                            color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.7)',
                                            bgcolor: isActive ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                                            '&:hover': { bgcolor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: 'inherit', minWidth: '30px' }}>
                                            {React.cloneElement(item.icon, { fontSize: 'small', sx: { fontSize: 18 } })}
                                        </ListItemIcon>
                                        <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActive ? 600 : 400 }} />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Collapse>
                </ListItem>

                {/* Menu Financeiro (Dropdown) */}
                <ListItem disablePadding sx={{ display: 'block', mb: 0.375, mt: 0.75 }}>
                    <ListItemButton
                        onClick={handleFinanceiroClick}
                        sx={{
                            borderRadius: '8px',
                            minHeight: 42,
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                        }}
                    >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: '34px' }}>
                            <AttachMoneyIcon />
                        </ListItemIcon>
                        <ListItemText primary="Financeiro" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 400 }} />
                        {openFinanceiro ? <ExpandLess sx={{ fontSize: '1.2rem' }} /> : <ExpandMore sx={{ fontSize: '1.2rem' }} />}
                    </ListItemButton>

                    <Collapse in={openFinanceiro} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {financeiroMenuItems.map((item) => {
                                const isActive = location.pathname.startsWith(item.path);
                                return (
                                    <ListItemButton
                                        key={item.text}
                                        onClick={() => { navigate(item.path); setMobileOpen(false); }}
                                        sx={{
                                            borderRadius: '8px',
                                            minHeight: 36,
                                            pl: 3.5,
                                            mb: 0.375,
                                            color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.7)',
                                            bgcolor: isActive ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                                            '&:hover': { bgcolor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                                        }}
                                    >
                                        <ListItemIcon sx={{ color: 'inherit', minWidth: '30px' }}>
                                                {item.icon ? 
                                                    React.cloneElement(item.icon, { fontSize: 'small', sx: { fontSize: 18 } }) : 
                                                    <AttachMoneyIcon fontSize="small" sx={{ fontSize: 18 }} />}
                                        </ListItemIcon>
                                        <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActive ? 600 : 400 }} />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Collapse>
                </ListItem>

                {/* Relatórios: item plano. O catálogo (/relatorios) tem busca e
                    categorias; as rotas por relatório continuam existindo e
                    mantêm a seção destacada. */}
                <ListItem disablePadding sx={{ display: 'block', mb: 0.375, mt: 0.75 }}>
                    <ListItemButton
                        onClick={() => { navigate('/relatorios'); setMobileOpen(false); }}
                        selected={location.pathname.startsWith('/relatorios')}
                        sx={{
                            borderRadius: '8px',
                            minHeight: 42,
                            color: location.pathname.startsWith('/relatorios')
                                ? 'primary.main' : 'rgba(255, 255, 255, 0.7)',
                            bgcolor: location.pathname.startsWith('/relatorios')
                                ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                            boxShadow: location.pathname.startsWith('/relatorios')
                                ? 'inset 3px 0 0 0 #1ABB9C' : 'none',
                            '&.Mui-selected': { bgcolor: 'rgba(255, 255, 255, 0.12)' },
                            '&.Mui-selected:hover': { bgcolor: 'rgba(255, 255, 255, 0.16)' },
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                        }}
                    >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: '34px' }}>
                            <AssessmentOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="Relatórios"
                            primaryTypographyProps={{
                                fontSize: '0.9rem',
                                fontWeight: location.pathname.startsWith('/relatorios') ? 600 : 400,
                            }}
                        />
                    </ListItemButton>
                </ListItem>

                {/* Menu Configurações (Dropdown) */}
                {configMenuItems.length > 0 && (
                    <>
                    <ListItem disablePadding sx={{ display: 'block', mb: 0.375, mt: 0.75 }}>
                        <ListItemButton
                            onClick={handleConfigClick}
                            sx={{
                                borderRadius: '8px',
                                minHeight: 42,
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                            }}
                        >
                            <ListItemIcon sx={{ color: 'inherit', minWidth: '34px' }}>
                                <SettingsOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText primary="Configurações" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 400 }} />
                            {openConfig ? <ExpandLess sx={{ fontSize: '1.2rem' }} /> : <ExpandMore sx={{ fontSize: '1.2rem' }} />}
                        </ListItemButton>

                        <Collapse in={openConfig} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {configMenuItems.map((item) => {
                                    const isActive = location.pathname.startsWith(item.path);
                                    return (
                                        <ListItemButton
                                            key={item.text}
                                            onClick={() => { navigate(item.path); setMobileOpen(false); }}
                                            sx={{
                                                borderRadius: '8px',
                                                minHeight: 36,
                                                pl: 3.5,
                                                mb: 0.375,
                                                color: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.7)',
                                                bgcolor: isActive ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                                                '&:hover': { bgcolor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)', color: 'primary.main' },
                                            }}
                                        >
                                            <ListItemIcon sx={{ color: 'inherit', minWidth: '30px' }}>
                                                {React.cloneElement(item.icon, { fontSize: 'small', sx: { fontSize: 18 } })}
                                            </ListItemIcon>
                                            <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: isActive ? 600 : 400 }} />
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </Collapse>
                    </ListItem>
                    </>
                )}
            </List>
        </Box>
    );

    const container = windowProp !== undefined ? () => windowProp().document.body : undefined;

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            {/* AppBar Mobile */}
            <AppBar
                position="fixed"
                elevation={1}
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                    display: modoFoco ? 'none' : { sm: 'none' },
                    bgcolor: 'background.paper',
                    color: 'text.primary'
                }}
            >
                <Toolbar>
                    <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        {getPageTitle()}
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* Drawer Responsivo */}
            <Box component="nav" sx={{ width: { sm: modoFoco ? 0 : drawerWidth }, flexShrink: { sm: 0 }, display: modoFoco ? 'none' : 'block' }}>
                <Drawer
                    container={container}
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, bgcolor: 'secondary.main', border: 'none', overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' } }}
                >
                    {drawerContent}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, bgcolor: 'secondary.main', borderRight: 'none', overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' } }}
                    open
                >
                    {drawerContent}
                </Drawer>
            </Box>

            {/* Conteúdo Principal */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    width: { xs: '100%', sm: modoFoco ? '100%' : `calc(100% - ${drawerWidth}px)` },
                    maxWidth: '100%',
                    minHeight: '100vh',
                    bgcolor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Toolbar sx={{ display: { sm: 'none' } }} />

                {/* Header Interno (Breadcrumbs + Perfil) */}
                <Box sx={{ px: { xs: 2, sm: 4 }, pt: { xs: 2, sm: 4 }, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? 1 : 0 }}>

                    {/* Breadcrumbs */}
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="Trilha de navegação" sx={{ mb: 0.5 }}>
                            <Link underline="hover" color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                SigaPsi
                            </Link>
                            {trilha.map((nivel, i) => {
                                const ultimo = i === trilha.length - 1;
                                if (ultimo || !nivel.path) {
                                    return (
                                        <Typography key={`${nivel.texto}-${i}`} color="text.primary">
                                            {nivel.texto}
                                        </Typography>
                                    );
                                }
                                return (
                                    <Link
                                        key={`${nivel.texto}-${i}`}
                                        underline="hover"
                                        color="inherit"
                                        onClick={() => navigate(nivel.path)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        {nivel.texto}
                                    </Link>
                                );
                            })}
                        </Breadcrumbs>
                    </Box>

                    {/* Perfil do Usuário */}
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: isMobile ? 1 : 2 }}>
                        <Tooltip title="Clique para acessar opções">
                            <IconButton
                                onClick={handleUserClick}
                                size={isMobile ? 'small' : 'medium'}
                                sx={{
                                    padding: isMobile ? '6px' : '8px',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {fotoBlobUrl ? (
                                    <Avatar
                                        src={fotoBlobUrl}
                                        sx={{
                                            width: isMobile ? 32 : 40,
                                            height: isMobile ? 32 : 40,
                                            cursor: 'pointer',
                                            border: '2px solid transparent',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                            }
                                        }}
                                    />
                                ) : (
                                    <Avatar sx={{
                                        width: isMobile ? 32 : 40,
                                        height: isMobile ? 32 : 40,
                                        bgcolor: 'secondary.main',
                                        color: 'primary.main',
                                        fontWeight: 'bold',
                                        fontSize: isMobile ? '1rem' : '1.2rem',
                                        cursor: 'pointer',
                                        border: '2px solid transparent',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                        }
                                    }}>
                                        {userData.initial}
                                    </Avatar>
                                )}
                            </IconButton>
                        </Tooltip>

                        {/* Menu Dropdown */}
                        <Menu
                            anchorEl={anchorEl}
                            open={openUserMenu}
                            onClose={handleUserClose}
                            PaperProps={{
                                // Aqui retiramos as bordas arredondadas e sombras manuais, o theme.js MuiPaper toma conta
                                sx: {
                                    overflow: 'visible',
                                    mt: 1.5,
                                    width: '350px',
                                },
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            {/* Header do Menu com Informações do Usuário */}
                            <Box sx={{
                                bgcolor: 'background.default',
                                px: 2.5,
                                py: 2.5,
                                display: 'flex',
                                gap: 1.5,
                                alignItems: 'center',
                                borderBottom: 1,
                                borderColor: 'divider'
                            }}>
                                {/* Avatar Grande */}
                                {fotoBlobUrl ? (
                                    <Avatar
                                        src={fotoBlobUrl}
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            border: 2,
                                            borderColor: 'primary.main'
                                        }}
                                    />
                                ) : (
                                    <Avatar sx={{
                                        width: 50,
                                        height: 50,
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        fontWeight: 'bold',
                                        fontSize: '1.5rem',
                                        border: 2,
                                        borderColor: 'primary.main'
                                    }}>
                                        {userData.initial}
                                    </Avatar>
                                )}

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 700,
                                            color: 'text.primary',
                                            whiteSpace: 'normal',
                                            wordWrap: 'break-word',
                                            overflow: 'visible',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {userData.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            display: 'block',
                                            whiteSpace: 'normal',
                                            wordWrap: 'break-word',
                                            overflow: 'visible'
                                        }}
                                    >
                                        @{userData.username}
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ m: 0 }} />

                            {/* Mobile Info Extra */}
                            <Box sx={{ px: 2, py: 1, display: { md: 'none' } }}>
                                <Typography variant="subtitle2" noWrap>{userData.name}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>{userData.username}</Typography>
                            </Box>
                            <Divider sx={{ display: { md: 'none' }, mb: 1 }} />

                            <MenuItem onClick={() => { navigate('/perfil'); handleUserClose(); }} sx={{ py: 1.5, px: 2.5, display: 'flex', gap: 1.5 }}>
                                <PersonOutlinedIcon sx={{ color: 'primary.main', fontSize: '1.3rem' }} />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Meu Perfil</Typography>
                                    <Typography variant="caption" color="text.secondary">Perfil e configurações</Typography>
                                </Box>
                            </MenuItem>

                            <MenuItem onClick={() => { navigate('/novidades'); handleUserClose(); }} sx={{ py: 1.5, px: 2.5, display: 'flex', gap: 1.5 }}>
                                <Badge color="error" variant="dot" invisible={!novidadesNaoVistas}>
                                    <NewReleasesIcon sx={{ color: 'primary.main', fontSize: '1.3rem' }} />
                                </Badge>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Novidades {novidadesNaoVistas && <Typography component="span" variant="caption" sx={{ color: '#dc2626', fontWeight: 700, ml: 0.5 }}>• Nova versão {versoes[0]?.versao}</Typography>}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Funcionalidades e atualizações</Typography>
                                </Box>
                            </MenuItem>

                            <Divider sx={{ my: 0.5 }} />

                            <MenuItem onClick={handleLogout} sx={{ py: 1.5, px: 2.5, color: 'error.main', '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' }, display: 'flex', gap: 1.5 }}>
                                <Logout sx={{ fontSize: '1.3rem' }} />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Sair do Sistema</Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Fazer logout</Typography>
                                </Box>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>

                {/* Conteúdo das Rotas */}
                <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4, flexGrow: 1, overflow: 'hidden' }}>
                    <Outlet />
                </Box>

            </Box>
        </Box>
    );
}

export default MainLayout;